import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function getSpacingStyles(props: Record<string, number | undefined | null>) {
    const style: Record<string, string> = {};
    for (const [key, value] of Object.entries(props)) {
        if (value !== undefined && value !== null) {
            style[key] = `${value}px`;
        }
    }
    return style;
}

export const getLength = (val: any, unit: any) => {
    if (unit === "auto") return "auto";
    if (val === undefined || val === null || val === "") return undefined;
    if (val === 0 || val === "0") return "0";
    if (!unit) return typeof val === "number" ? `${val}px` : val;
    return `${val}${unit}`;
};

export function getUniversalStyles(props: Record<string, any>): React.CSSProperties {
    const {
        // Dimensions
        dimensions, width, height,
        // Spacing
        padding, padding_y, padding_x,
        paddingTop, paddingBottom, paddingLeft, paddingRight,
        margin, margin_y, margin_x,
        marginTop, marginBottom, marginLeft, marginRight,
        gap,
        // Border
        border, borderRadius, borderWidth, borderColor, borderStyle,
        // Layout / Flex
        display, flexDirection, justifyContent, alignItems, flexWrap, flex, alignSelf,
        // Background
        backgroundColor, backgroundImage, backgroundSize, backgroundPosition,
        // Typography
        textAlign, color, fontSize, fontWeight, fontFamily, lineHeight, letterSpacing,
        // Position
        position, top, left, right, bottom, zIndex,
        // Misc
        opacity, overflow, boxShadow, visibility, transform,
    } = props;

    // Helper to extract dimension safely
    const getDim = (dimObj: any, fallback: any) => {
        if (!dimObj) return fallback ? getLength(fallback, undefined) : undefined;
        return getLength(dimObj.value, dimObj.unit);
    };

    const style: React.CSSProperties = {
        // Dimensions
        width: dimensions?.width ? getDim(dimensions.width, width) : (width ? getLength(width, undefined) : undefined),
        height: dimensions?.height ? getDim(dimensions.height, height) : (height ? getLength(height, undefined) : undefined),

        // Spacing - Padding (Priority: padding obj > granular prop > legacy prop)
        paddingTop: padding ? getLength(padding.top, padding.unit) : (paddingTop !== undefined ? getLength(paddingTop, "px") : (padding_y ? getLength(padding_y, undefined) : undefined)),
        paddingRight: padding ? getLength(padding.right, padding.unit) : (paddingRight !== undefined ? getLength(paddingRight, "px") : (padding_x ? getLength(padding_x, undefined) : undefined)),
        paddingBottom: padding ? getLength(padding.bottom, padding.unit) : (paddingBottom !== undefined ? getLength(paddingBottom, "px") : (padding_y ? getLength(padding_y, undefined) : undefined)),
        paddingLeft: padding ? getLength(padding.left, padding.unit) : (paddingLeft !== undefined ? getLength(paddingLeft, "px") : (padding_x ? getLength(padding_x, undefined) : undefined)),

        // Spacing - Margin
        marginTop: margin ? getLength(margin.top, margin.unit) : (marginTop !== undefined ? getLength(marginTop, "px") : (margin_y ? getLength(margin_y, undefined) : undefined)),
        marginRight: margin ? getLength(margin.right, margin.unit) : (marginRight !== undefined ? getLength(marginRight, "px") : (margin_x ? getLength(margin_x, undefined) : undefined)),
        marginBottom: margin ? getLength(margin.bottom, margin.unit) : (marginBottom !== undefined ? getLength(marginBottom, "px") : (margin_y ? getLength(margin_y, undefined) : undefined)),
        marginLeft: margin ? getLength(margin.left, margin.unit) : (marginLeft !== undefined ? getLength(marginLeft, "px") : (margin_x ? getLength(margin_x, undefined) : undefined)),

        gap: gap ? getLength(gap, "px") : undefined,

        // Border
        borderWidth: border ? `${border.width}px` : (borderWidth ? getLength(borderWidth, "px") : undefined),
        borderStyle: border?.style || borderStyle,
        borderColor: border?.color || borderColor,
        borderRadius: border ? `${border.radius}px` : (borderRadius ? getLength(borderRadius, "px") : undefined),

        // Flex/Layout
        display, flexDirection, justifyContent, alignItems, flexWrap, flex, alignSelf,

        // Background
        backgroundColor, backgroundImage, backgroundSize, backgroundPosition,

        // Typography
        textAlign, color,
        fontSize: fontSize ? getLength(fontSize, "px") : undefined,
        fontWeight, fontFamily, lineHeight,
        letterSpacing: letterSpacing ? getLength(letterSpacing, "px") : undefined,

        // Position
        position: position as any, top, left, right, bottom, zIndex,

        // Misc
        opacity, overflow, boxShadow, visibility, transform,
    };

    // Clean undefined values
    Object.keys(style).forEach(key => (style as any)[key] === undefined && delete (style as any)[key]);

    return style;
}

export const getValidImageUrl = (url?: string | null) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('/')) return url;
    if (process.env.NEXT_PUBLIC_SUPABASE_URL && url.includes('store-assets')) {
        return `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/${url.startsWith('/') ? url.substring(1) : url}`;
    }
    return url;
};
